import React, { useState, useEffect, useCallback, useContext, createContext, useRef } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import StatusPieChart from '../common/StatusPieChart';
import { usePostHog } from '@posthog/react';
import { trackFundingView } from '../../utils/analytics';
import { getCompetitiveStyle, getFundingTypeStyle, THEME, getStatusStyle, protectedLandPill } from '../../utils/constants';
import { fmtCurrency, fmtRate, getAllEarnings, formatDate, getDeadlineDisplay, getOpensDisplay, getClosesDisplay } from '../../utils/helpers';
import AuthSuccessMessage from '../auth/AuthSuccessMessage';
import FeedbackForm from './FeedbackForm';
import SaveButton from './SaveButton';
import AlertButton from './AlertButton';
import PrintSectionModal from './PrintSectionModal';
import FundingPrintLayout from './FundingPrintLayout';
import FundingRow from './FundingRow';
import BetaBanner from '../common/BetaBanner';
import ReactMarkdown from 'react-markdown';

const ListOrderedContext = createContext(false);
const ListDepthContext = createContext(0);

const SectionBullet = ({ children }) => {
  const isOrdered = useContext(ListOrderedContext);
  const depth = useContext(ListDepthContext);
  return isOrdered ? (
    <li style={{ fontSize: '0.85rem', color: '#7C7B7B', lineHeight: 1.6, padding: '3px 0' }}>{children}</li>
  ) : (
    <li style={{ fontSize: '0.82rem', color: '#7C7B7B', lineHeight: 1.55, padding: '4px 0 4px 18px', position: 'relative' }}>
      {depth > 1
        ? <span style={{ position: 'absolute', left: 2, color: '#615DEC', fontSize: '0.55rem', top: 8, fontWeight: 700 }}>—</span>
        : <span style={{ position: 'absolute', left: 2, top: 11, width: 7, height: 7, borderRadius: '50%', border: '1.5px solid #615DEC', background: 'transparent', display: 'inline-block' }} />
      }
      {children}
    </li>
  );
};

const MarkdownLi = ({ children }) => {
  const isOrdered = useContext(ListOrderedContext);
  return isOrdered ? (
    <li style={{ fontSize: '0.85rem', color: '#7C7B7B', lineHeight: 1.6, padding: '3px 0' }}>
      {children}
    </li>
  ) : (
    <li style={{ fontSize: '0.82rem', color: '#7C7B7B', lineHeight: 1.55, padding: '4px 0 4px 18px', position: 'relative' }}>
      <span style={{ position: 'absolute', left: 2, color: '#615DEC', fontSize: '0.55rem', top: 8, fontWeight: 700 }}>—</span>
      {children}
    </li>
  );
};

const mdHeading      = { fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.8px',  textTransform: 'uppercase', color: '#615DEC', margin: '12px 0 6px' };
const mdHeadingGrey  = { fontSize: '0.66rem', fontWeight: 600, letterSpacing: '1.4px',  textTransform: 'uppercase', color: '#7C7B7B', margin: '14px 0 4px' };
const mdParagraph    = { fontSize: '0.85rem', color: '#7C7B7B', lineHeight: 1.7, margin: '4px 0' };

const sectionMarkdownComponents = {
  h1: ({ children }) => <p style={mdHeading}>{children}</p>,
  h2: ({ children }) => <p style={mdHeading}>{children}</p>,
  h3: ({ children }) => <p style={mdHeading}>{children}</p>,
  ol: ({ children, start }) => (
    <ListOrderedContext.Provider value={true}>
      <ol start={start} style={{ listStyleType: 'decimal', paddingLeft: 22, margin: 0 }}>{children}</ol>
    </ListOrderedContext.Provider>
  ),
  ul: ({ children }) => (
    <ListDepthContext.Consumer>
      {depth => (
        <ListDepthContext.Provider value={depth + 1}>
          <ListOrderedContext.Provider value={false}>
            <ul style={{ listStyle: 'none', padding: '2px 0 4px 14px', margin: '4px 0 0 6px' }}>{children}</ul>
          </ListOrderedContext.Provider>
        </ListDepthContext.Provider>
      )}
    </ListDepthContext.Consumer>
  ),
  li: SectionBullet,
  p: ({ children }) => <p style={mdParagraph}>{children}</p>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#615DEC', textDecoration: 'underline' }}>
      {children}
    </a>
  ),
};

const EvidenceLi = ({ children }) => (
  <li style={{ fontSize: '0.85rem', color: '#7C7B7B', lineHeight: 1.5, padding: '7px 0 7px 28px', position: 'relative', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
    <span style={{ position: 'absolute', left: 0, color: '#615DEC', fontSize: '1rem', top: 6 }}>☐</span>
    {children}
  </li>
);

const evidenceMarkdownComponents = {
  ...sectionMarkdownComponents,
  h1: ({ children }) => <p style={mdHeadingGrey}>{children}</p>,
  h2: ({ children }) => <p style={mdHeadingGrey}>{children}</p>,
  h3: ({ children }) => <p style={mdHeadingGrey}>{children}</p>,
  ul: ({ children }) => <ul style={{ listStyle: 'none', padding: 0 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ listStyle: 'none', padding: 0 }}>{children}</ol>,
  li: EvidenceLi,
};

const howToApplyComponents = {
  ...sectionMarkdownComponents,
  h1: ({ children }) => <p style={mdHeadingGrey}>{children}</p>,
  h2: ({ children }) => <p style={mdHeadingGrey}>{children}</p>,
  h3: ({ children }) => <p style={mdHeadingGrey}>{children}</p>,
  ul: ({ children }) => (
    <ListOrderedContext.Provider value={false}>
      <ul style={{ listStyle: 'none', padding: '6px 0 4px 14px', margin: '4px 0 0 6px', borderLeft: '3px solid rgba(97,93,236,0.3)', background: 'rgba(97,93,236,0.03)', borderRadius: '0 4px 4px 0' }}>
        {children}
      </ul>
    </ListOrderedContext.Provider>
  ),
  li: MarkdownLi,
};

const C = THEME;

// ─── sub-components ─────────────────────────────────────────────────────────

const TierLabel = ({ children }) => (
  <div style={{
    fontSize: '0.65rem', fontWeight: 700, letterSpacing: 2,
    textTransform: 'uppercase', color: C.textMid,
    margin: '28px 0 8px', display: 'flex', alignItems: 'center', gap: 10,
  }}>
    {children}
    <span style={{ flex: 1, height: 1, background: C.border }} />
  </div>
);

const KiCard = ({ label, children, style }) => (
  <div style={{
    background: C.white, border: `1px solid ${C.border}`,
    borderRadius: 10, padding: '14px 16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', ...style,
  }}>
    <div style={{
      fontSize: '0.66rem', fontWeight: 600, letterSpacing: '1.4px',
      textTransform: 'uppercase', color: C.textMid, marginBottom: 4,
    }}>{label}</div>
    <div style={{ fontSize: '0.88rem', color: C.textDark, fontWeight: 500, lineHeight: 1.4 }}>
      {children}
    </div>
  </div>
);

const AimTag = ({ children, style }) => (
  <span style={{
    background: C.primaryLight, color: C.primary,
    borderRadius: 20, padding: '3px 10px',
    fontSize: '0.72rem', fontWeight: 500, ...style,
  }}>
    {children}
  </span>
);

const SubHeading = ({ children, style }) => (
  <p style={{
    fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.8px',
    color: C.primary, textTransform: 'uppercase',
    margin: '16px 0 8px', paddingBottom: 5,
    borderBottom: `1px solid ${C.border}`, ...style,
  }}>{children}</p>
);

const InfoBox = ({ children }) => (
  <div style={{
    background: C.primaryLight,
    borderLeft: `3px solid ${C.primary}`,
    borderRadius: '0 6px 6px 0',
    padding: '10px 14px', fontSize: '0.82rem',
    color: C.primary, marginBottom: 10, lineHeight: 1.5,
  }}>
    {children}
  </div>
);

const infoBoxMarkdownComponents = {
  ...sectionMarkdownComponents,
  p: ({ children }) => <p style={{ fontSize: '0.85rem', color: C.primary, lineHeight: 1.7, margin: '4px 0' }}>{children}</p>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: C.primaryDark, textDecoration: 'underline', fontWeight: 500 }}>
      {children}
    </a>
  ),
};

const SectionBlock = ({ id, icon, title, hint, badge, openSections, onToggle, children }) => {
  const isOpen = !!openSections[id];
  return (
    <div style={{
      marginTop: 12, border: `1px solid ${C.border}`, borderRadius: 10,
      background: C.white, overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      <button
        onClick={() => onToggle(id)}
        aria-expanded={isOpen}
        style={{
          width: '100%', background: isOpen ? 'rgba(97,93,236,0.04)' : 'none',
          border: 'none', padding: '16px 20px',
          display: 'flex', alignItems: 'center', gap: 12,
          cursor: 'pointer', textAlign: 'left',
          borderBottom: isOpen ? `1px solid ${C.border}` : '1px solid transparent',
          fontFamily: 'inherit', transition: 'background 0.15s',
        }}
      >
        <div style={{
          width: 34, height: 34, borderRadius: 8, flexShrink: 0,
          backgroundColor: isOpen ? C.primary : C.primaryLight,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1rem', transition: 'background 0.2s',
        }}>
          {icon}
        </div>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: '0.9rem', fontWeight: 600, color: C.textDark, display: 'block' }}>
            {title}
          </span>
          {hint && (
            <span style={{ fontSize: '0.73rem', color: C.textMid, marginTop: 1, display: 'block' }}>
              {hint}
            </span>
          )}
        </div>
        {badge && (
          <span style={{
            background: C.primaryLight, color: C.primary,
            borderRadius: 12, padding: '2px 9px',
            fontSize: '0.7rem', fontWeight: 600, flexShrink: 0,
          }}>
            {badge}
          </span>
        )}
        <svg
          style={{
            width: 18, height: 18, color: C.textMid, flexShrink: 0,
            transition: 'transform 0.25s ease',
            transform: isOpen ? 'rotate(180deg)' : 'none',
          }}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {isOpen && (
        <div style={{ padding: 20 }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ─── helpers ────────────────────────────────────────────────────────────────

const parseListField = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data.filter(Boolean);
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch (_) {}
    return data.split('\n').map(s => s.trim()).filter(Boolean);
  }
  return [];
};

const toMarkdownString = (data) => {
  if (!data) return '';
  if (Array.isArray(data)) return data.filter(Boolean).map(item => `- ${item}`).join('\n');
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) return parsed.filter(Boolean).map(item => `- ${item}`).join('\n');
    } catch (_) {}
    return data;
  }
  return '';
};



const getLocationText = (funding) => {
  if (funding.country) {
    if (Array.isArray(funding.country)) return funding.country.join(', ');
    return funding.country;
  }
  const locs = [];
  if (funding.location_england) locs.push('England');
  if (funding.location_scotland) locs.push('Scotland');
  if (funding.location_wales) locs.push('Wales');
  if (funding.location_northern_ireland) locs.push('Northern Ireland');
  if (locs.length > 0) return locs.join(', ');
  if (funding.location?.length > 0) return funding.location.join(', ');
  return 'Not specified';
};


// ─── main component ─────────────────────────────────────────────────────────

const ALL_SECTION_IDS = [
  'whatToDo', 'evidence', 'money', 'compatible',
  'whoCanApply', 'howToApply', 'criteria', 'consents', 'contacts',
];

const FundingDetail = () => {
  const { slug } = useParams();
  const location = useLocation();
  const posthog = usePostHog();
  const [funding, setFunding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [expandedPots, setExpandedPots] = useState({});
  const [showAutoProtectedLand, setShowAutoProtectedLand] = useState(false);
  const [scrollPct, setScrollPct] = useState(0);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [heroVisible, setHeroVisible] = useState(true);
  const printRef = useRef(null);
  const heroRef = useRef(null);

  const urlParams = new URLSearchParams(location.search);
  const isFromAuth = urlParams.get('apply') === 'true';

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const pct = (h.scrollTop || document.body.scrollTop) / (h.scrollHeight - h.clientHeight) * 100;
      setScrollPct(Math.min(100, pct));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only hide when sentinel has scrolled above the viewport (not below)
        if (!entry.isIntersecting) {
          setHeroVisible(entry.boundingClientRect.top >= 0);
        } else {
          setHeroVisible(true);
        }
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [funding]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchFunding(); }, [slug]);

  useEffect(() => {
    if (funding) {
      trackFundingView(funding.id, funding.name || funding.description);
      posthog.capture('funding_detail_viewed', {
        funding_id: funding.id,
        funding_name: funding.name,
        funding_slug: funding.slug,
      });
      if (isFromAuth) {
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 5000);
      }
    }
  }, [funding, isFromAuth, posthog]);

  const fetchFunding = async () => {
    try {
      const response = await api.get(`/funding-schemes/${slug}/`);
      setFunding(response.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError('Failed to load funding details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToggle = (fundingId, newSaveState) => {
    setFunding(prev => ({ ...prev, is_favourite: newSaveState }));
  };

  const [printSections, setPrintSections] = useState(null);

  const handlePrint = (selectedSections) => {
    setShowPrintModal(false);
    setPrintSections(selectedSections);
  };

  useEffect(() => {
    if (!printSections || !printRef.current) return;
    const timer = setTimeout(() => {
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;
      const content = printRef.current.innerHTML;
      const safeTitle = (funding?.name || 'funding').replace(/[^a-z0-9]/gi, '-').toLowerCase().replace(/-+/g, '-').replace(/^-|-$/g, '');
      const exportDate = formatDate(new Date().toISOString());
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${safeTitle}</title>
          <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet">
          <style>
            *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Poppins', system-ui, sans-serif; }

            @page {
              size: A4;
              margin: 12mm 12mm 18mm 12mm;
            }

            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }

              /* ── Page break rules ─────────────────────────── */

              /* Each major section: avoid breaking inside if possible.
                 Browser will push to next page if it fits, or break
                 naturally if too long. */
              .print-section {
                page-break-inside: avoid;
                break-inside: avoid;
              }

              /* Tier labels ("Detail", "Application") should never
                 appear alone at the bottom of a page */
              .tier-label {
                page-break-after: avoid;
                break-after: avoid;
              }

              /* Section header bar must not separate from body */
              .section-header {
                page-break-after: avoid;
                break-after: avoid;
              }

              /* Small elements: cards, table rows, individual items */
              .avoid-break {
                page-break-inside: avoid;
                break-inside: avoid;
              }

              /* Headings should not be orphaned at page bottom */
              h1, h2, h3, p[class] {
                page-break-after: avoid;
                break-after: avoid;
              }

              /* Basic widows/orphans */
              p { orphans: 3; widows: 3; }
            }

            /* Oko footer — repeats on every printed page */
            .print-footer {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              padding: 0;
              display: flex;
              align-items: center;
              justify-content: space-between;
              font-family: 'Poppins', system-ui, sans-serif;
              font-size: 0.55rem;
              color: #7C7B7B;
            }
            .print-footer .oko-brand {
              font-weight: 600;
              color: #615DEC;
              letter-spacing: 0.5px;
            }
          </style>
        </head>
        <body>
          <div class="print-footer">
            <span class="oko-brand">oko.ag</span>
            <span>Exported ${exportDate}</span>
          </div>
          ${content}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      };
      setPrintSections(null);
    }, 100);
    return () => clearTimeout(timer);
  }, [printSections, funding]);

  const toggleSection = useCallback((sectionId) => {
    setOpenSections(prev => {
      const next = { ...prev, [sectionId]: !prev[sectionId] };
      const openCount = ALL_SECTION_IDS.filter(s => next[s]).length;
      setAllExpanded(openCount === ALL_SECTION_IDS.length);
      return next;
    });
  }, []);

  const toggleAll = () => {
    const next = !allExpanded;
    const newState = {};
    ALL_SECTION_IDS.forEach(s => { newState[s] = next; });
    setOpenSections(newState);
    setAllExpanded(next);
  };

  const togglePot = (pot) => {
    setExpandedPots(prev => ({ ...prev, [pot]: !prev[pot] }));
  };

  // ── loading / error ──────────────────────────────────────────────────────
  if (loading) return <LoadingSpinner size="large" />;

  if (error) return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>
    </div>
  );

  if (!funding) return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 text-center">
      <h2 className="text-2xl font-bold text-gray-900">Funding opportunity not found</h2>
      <Link to="/funding" className="mt-4 inline-block text-sm" style={{ color: C.primary }}>
        ← Back to funding opportunities
      </Link>
    </div>
  );

  // ── derived data ─────────────────────────────────────────────────────────
  const allEarnings = getAllEarnings(funding.payment_rates);
  const statusStyle    = getStatusStyle(funding.status);
  const competitiveArr = Array.isArray(funding.competitive) ? funding.competitive : (funding.competitive ? [funding.competitive] : []);
  const deliveryPartners = (funding.contacts || []).filter(c =>
    c.role && (c.role.toLowerCase().includes('delivery') || c.role.toLowerCase().includes('partner'))
  );
  const evidenceItems  = parseListField(funding.evidence_requirements).filter(item => !item.startsWith('#'));
  const expectedCosts  = (funding.costs || []).filter(c => c.cost_type !== 'ineligible');
  const ineligibleCosts = (funding.costs || []).filter(c => c.cost_type === 'ineligible');
  const fundCollections = funding.fund_collection || [];
  const heroCode = fundCollections.length > 0
    ? fundCollections.join(' · ')
    : (funding.fund_group ? (Array.isArray(funding.fund_group) ? funding.fund_group.join(' · ') : funding.fund_group) : null);

  const hasWhatToDo   = !!(funding.must_do || funding.must_not_do);
  const hasEvidence   = evidenceItems.length > 0;
  const hasMoney      = allEarnings.length > 0 || funding.payment_model || funding.costs?.length > 0;
  const hasCompatible = (funding.compatible_grants_display?.length > 0) || (Object.keys(funding.compatible_schemes || {}).length > 0);
  const hasWhoCanApply = funding.who_can_apply || funding.eligibility_requirements || funding.ineligibility_requirements;
  const hasHowToApply  = !!funding.how_to_apply;
  const hasCriteria    = !!funding.application_criteria;
  const hasConsents    = funding.protected_land?.length > 0 || funding.consent_text;
  const hasContacts    = funding.contacts?.length > 0 || funding.application_url || funding.supporting_documentation_urls;

  const pageWrapStyle = {
    maxWidth: 780, margin: '0 auto', padding: '0 20px',
  };

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <div id="funding-detail-content" style={{ minHeight: '100vh', backgroundColor: '#faf9f5', fontFamily: "'Poppins', system-ui, sans-serif", paddingBottom: 60 }}>

      {/* Scroll progress bar */}
      <div style={{ height: 3, background: C.border, position: 'sticky', top: 0, zIndex: 99 }}>
        <div style={{ height: '100%', background: C.primary, width: `${scrollPct}%`, transition: 'width 0.1s' }} />
      </div>

      {/* Condensed header — fixed, slides in after hero scrolls out of view */}
      <div style={{
        position: 'fixed', top: 3, left: 0, right: 0, zIndex: 98,
        background: `linear-gradient(135deg, ${C.primaryDeep} 0%, ${C.primaryMid} 60%, ${C.primary} 100%)`,
        padding: '10px 20px',
        display: 'flex', alignItems: 'center',
        transform: heroVisible ? 'translateY(-110%)' : 'translateY(0)',
        transition: 'transform 0.25s ease',
        pointerEvents: heroVisible ? 'none' : 'auto',
        boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {heroCode && (
            <div style={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: 2, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {heroCode}
            </div>
          )}
          <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3 }}>
            {funding?.name || funding?.description}
          </div>
          {funding?.funders?.length > 0 && (
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.65)', fontWeight: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {funding.funders.join(', ')}
            </div>
          )}
        </div>
      </div>

      <BetaBanner
        variant="detail"
        onReportIssue={() => setShowFeedbackForm(true)}
      />

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <div style={{
        background: `linear-gradient(135deg, ${C.primaryDeep} 0%, ${C.primaryMid} 60%, ${C.primary} 100%)`,
        padding: '36px 20px 28px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={pageWrapStyle}>
          {/* Back link + actions row */}
          <div className="pdf-hidden" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <Link
              to="/funding"
              style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem', textDecoration: 'none' }}
              onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
            >
              ← Back to funding opportunities
            </Link>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => setShowPrintModal(true)}
              style={{
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                color: 'rgba(255,255,255,0.85)', borderRadius: 6, padding: '5px 12px',
                fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              🖨 Print
            </button>
            <AlertButton schemeId={funding.id} variant="light" />
            <SaveButton funding={funding} onSaveToggle={handleSaveToggle} />
            </div>
          </div>

          {heroCode && (
            <div style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: 2, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', marginBottom: 6 }}>
              {heroCode}
            </div>
          )}

          <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#fff', lineHeight: 1.2, marginBottom: 8 }}>
            {funding.name || funding.description}
          </h1>

          {funding.funders?.length > 0 && (
            <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.65)', marginBottom: 18, fontWeight: 300 }}>
              {funding.funders.join(', ')}
            </p>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {statusStyle && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '4px 12px', borderRadius: 20,
                fontSize: '0.75rem', fontWeight: 600,
                background: statusStyle.bg, color: statusStyle.color,
              }}>
                {statusStyle.label}
              </span>
            )}
            {competitiveArr.map((c, i) => {
              const cs = getCompetitiveStyle(c);
              return (
                <span key={i} style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '4px 12px', borderRadius: 20,
                  fontSize: '0.75rem', fontWeight: 600,
                  background: cs.bg, color: cs.color,
                }}>
                  {cs.label}
                </span>
              );
            })}
            {funding.funding_type && (() => {
              const fts = getFundingTypeStyle(funding.funding_type);
              return (
                <span style={{
                  padding: '4px 12px', borderRadius: 20,
                  fontSize: '0.75rem', fontWeight: 600,
                  background: fts.bg, color: fts.color,
                }}>
                  {fts.label}
                </span>
              );
            })()}
          </div>
        </div>
        {/* Sentinel — observed to detect when hero bottom leaves viewport */}
        <div ref={heroRef} style={{ height: 0 }} />
      </div>

      {/* ── Earning strip ─────────────────────────────────────────────── */}
      {(allEarnings.length > 0 || funding.duration || getDeadlineDisplay(funding)) && (
        <div style={{ background: C.white, borderBottom: `2px solid ${C.border}` }}>
          <div style={pageWrapStyle}>
            <div style={{ display: 'flex', alignItems: 'stretch' }}>
              {allEarnings.length > 0 && (
                <div style={{ display: 'flex', borderRight: `1px solid ${C.border}` }}>
                  {allEarnings.map((earning, i) => (
                    <div key={i} style={{ padding: '18px 20px 18px', paddingLeft: i === 0 ? 24 : 12 }}>
                      {i === 0 ? (
                        <div style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', color: C.textMid, marginBottom: 4 }}>
                          💰 Earning figure
                        </div>
                      ) : (
                        <div style={{ height: 19 }} />
                      )}
                      <div style={{ fontSize: '2rem', color: C.primaryDeep, lineHeight: 1, marginBottom: 2, fontWeight: 700 }}>
                        {earning.value}
                      </div>
                      {earning.unit && (
                        <div style={{ fontSize: '0.78rem', color: C.textMid }}>{earning.unit}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 'auto' }}>
                <div style={{ padding: '12px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '1rem' }}>📅</span>
                  <div style={{ fontSize: '0.8rem', color: C.textMid }}>
                    <strong style={{ color: C.textDark, display: 'block', fontSize: '0.75rem' }}>Deadline</strong>
                    {getDeadlineDisplay(funding)}
                  </div>
                </div>
                {funding.duration && (
                  <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: '1rem' }}>⏱</span>
                    <div style={{ fontSize: '0.8rem', color: C.textMid }}>
                      <strong style={{ color: C.textDark, display: 'block', fontSize: '0.75rem' }}>Duration</strong>
                      {funding.duration}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Page content ──────────────────────────────────────────────── */}
      <div style={pageWrapStyle}>

        {showSuccessMessage && (
          <div style={{ marginTop: 16 }}>
            <AuthSuccessMessage
              action={isFromAuth ? 'apply for funding' : 'view funding details'}
              fundingId={funding.slug || funding.id}
              fundingName={funding.name || funding.description}
            />
          </div>
        )}

        {/* ── Overview ─────────────────────────────────────────────────── */}
        <TierLabel>Overview</TierLabel>

        {/* Funder + delivery partner row */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <KiCard label="🏛 Funder" style={{ minWidth: 160, maxWidth: 300 }}>
            {funding.funders?.length > 0 ? funding.funders.join('; ') : 'Not specified'}
          </KiCard>
          {deliveryPartners.length > 0 && (
            <KiCard label="🤝 Delivery Partner" style={{ minWidth: 160, maxWidth: 300 }}>
              {deliveryPartners.map(c => c.name).join('; ')}
            </KiCard>
          )}
          {funding.is_supplementary && (
            <div style={{
              background: '#615DEC', border: '1px solid #615DEC',
              borderRadius: 10, padding: '14px 16px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              minWidth: 160, maxWidth: 340,
            }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '3px 10px', borderRadius: 20,
                fontSize: '0.7rem', fontWeight: 600,
                color: '#615DEC', border: '1.5px solid #fff',
                background: '#fff',
              }}>
                Supplementary
              </span>
              <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.4, marginTop: 6 }}>
                This is a supplementary action. It can only be used with an eligible base action.
              </p>
            </div>
          )}
        </div>

        {/* Country / dates / rotational / parcel row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 10 }}>
          <KiCard label="🌍 Country">
            {getLocationText(funding)}
          </KiCard>
          {(getOpensDisplay(funding) || getClosesDisplay(funding) || funding.deadline_date || funding.deadline_tag) && (
            <KiCard label="📅 Key Dates">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {getOpensDisplay(funding) && (
                  <div><strong style={{ color: C.textDark }}>Opens:</strong> {getOpensDisplay(funding)}</div>
                )}
                {getClosesDisplay(funding) && (
                  <div><strong style={{ color: C.textDark }}>Closes:</strong> {getClosesDisplay(funding)}</div>
                )}
                {(funding.deadline_date || funding.deadline_tag) && (
                  <div><strong style={{ color: C.textDark }}>Deadline:</strong> {getDeadlineDisplay(funding)}</div>
                )}
              </div>
            </KiCard>
          )}
          {Array.isArray(funding.rotational_or_static) && funding.rotational_or_static.filter(v => v && v.toLowerCase() !== 'not relevant').length > 0 && (
            <KiCard label="🔄 Rotational / Static">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
                {funding.rotational_or_static.filter(v => v && v.toLowerCase() !== 'not relevant').map((v, i) => (
                  <AimTag key={i}>{v}</AimTag>
                ))}
              </div>
            </KiCard>
          )}
          {funding.parcel_area_available?.filter(p => p && p.toLowerCase() !== 'not relevant').length > 0 && (
            <KiCard label="📐 Parcel Area">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
                {funding.parcel_area_available.filter(p => p && p.toLowerCase() !== 'not relevant').map((p, i) => (
                  <AimTag key={i}>{p}</AimTag>
                ))}
              </div>
            </KiCard>
          )}
        </div>

        {/* Summary card */}
        {(funding.executive_summary || funding.description) && (
          <div style={{
            background: C.white, border: `1px solid ${C.border}`,
            borderRadius: 10, padding: 24, marginTop: 20,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}>
            <ReactMarkdown components={sectionMarkdownComponents}>{funding.executive_summary || funding.description}</ReactMarkdown>
          </div>
        )}

        {/* Classification cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginTop: 10 }}>

          {funding.land_types?.filter(t => t && t.toLowerCase() !== 'not specified').length > 0 && (
            <KiCard label="🗺 Eligible Land Type & Use" style={{ gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 4 }}>
                {funding.land_types.map((t, i) => <AimTag key={i}>{t}</AimTag>)}
              </div>
            </KiCard>
          )}
          {funding.aims?.length > 0 && (
            <KiCard label="🎯 Aim Tags" style={{ gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 4 }}>
                {funding.aims.map((a, i) => <AimTag key={i}>{a}</AimTag>)}
              </div>
            </KiCard>
          )}
        </div>

        {/* Expand all row */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '20px 0 4px' }}>
          <button
            onClick={toggleAll}
            style={{
              background: 'none', border: `1px solid ${C.border}`,
              borderRadius: 6, padding: '6px 14px',
              fontSize: '0.75rem', color: C.textMid,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {allExpanded ? '⊟  Collapse all' : '⊞  Expand all sections'}
          </button>
        </div>

        {/* ── Detail sections ───────────────────────────────────────────── */}
        <TierLabel>Detail</TierLabel>

        {hasWhatToDo && (
          <SectionBlock
            id="whatToDo" icon="📋"
            title="What To Do & Not Do"
            hint="Management requirements, must do and must not do"
            openSections={openSections} onToggle={toggleSection}
          >
            {funding.must_do && (
              <>
                <SubHeading style={{ marginTop: 0, fontSize: '0.66rem', letterSpacing: '1.4px', color: C.textMid, borderBottom: 'none', paddingBottom: 0 }}>Must Do</SubHeading>
                <ReactMarkdown components={sectionMarkdownComponents}>{toMarkdownString(funding.must_do)}</ReactMarkdown>
              </>
            )}
            {funding.must_not_do && (
              <>
                <div style={{ borderTop: '1px solid rgba(0,0,0,0.07)', margin: '14px 0 0' }} />
                <SubHeading style={{ fontSize: '0.66rem', letterSpacing: '1.4px', color: C.textMid, borderBottom: 'none', paddingBottom: 0 }}>Must Not Do</SubHeading>
                <ReactMarkdown components={sectionMarkdownComponents}>{toMarkdownString(funding.must_not_do)}</ReactMarkdown>
              </>
            )}
          </SectionBlock>
        )}

        {hasEvidence && (
          <SectionBlock
            id="evidence" icon="📁"
            title="Evidence Requirements"
            hint="What you must keep and when"
            badge={evidenceItems.length > 0 ? `${evidenceItems.length} items` : undefined}
            openSections={openSections} onToggle={toggleSection}
          >
            <ReactMarkdown components={evidenceMarkdownComponents}>{toMarkdownString(funding.evidence_requirements)}</ReactMarkdown>
          </SectionBlock>
        )}

        {hasMoney && (
          <SectionBlock
            id="money" icon="💷"
            title="Money"
            hint="Earnings, expected costs, payment details"
            openSections={openSections} onToggle={toggleSection}
          >
            {/* Payment rates */}
            {funding.payment_rates?.length > 0 && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
                  {funding.payment_rates.map((r, i) => {
                    const isPercent = r.unit === '% of costs';
                    const val = (r.rate_minimum === r.rate_maximum || !r.rate_maximum)
                      ? fmtRate(r.rate_minimum || r.rate_maximum, r.unit)
                      : `${fmtRate(r.rate_minimum, r.unit)} – ${fmtRate(r.rate_maximum, r.unit)}`;
                    return (
                      <div key={i} style={{ background: C.pale, border: `1px solid ${C.border}`, borderRadius: 6, padding: '12px 14px' }}>
                        <div style={{ fontSize: '0.66rem', fontWeight: 600, letterSpacing: '1.2px', textTransform: 'uppercase', color: C.textMid, marginBottom: 5 }}>
                          {r.short_description || `Rate ${i + 1}`}
                        </div>
                        <div style={{ fontSize: '1.5rem', color: C.primaryDeep, fontWeight: 700 }}>{val}</div>
                        {!isPercent && r.unit && <div style={{ fontSize: '0.78rem', color: C.textMid, marginTop: 4 }}>{r.unit}</div>}
                      </div>
                    );
                  })}
                  {funding.payment_model && (
                    <div style={{ background: C.pale, border: `1px solid ${C.border}`, borderRadius: 6, padding: '12px 14px' }}>
                      <div style={{ fontSize: '0.66rem', fontWeight: 600, letterSpacing: '1.2px', textTransform: 'uppercase', color: C.textMid, marginBottom: 5 }}>
                        Payment method
                      </div>
                      <div style={{ fontSize: '1.1rem', color: C.primaryDeep, fontWeight: 600, paddingTop: 4 }}>{funding.payment_model}</div>
                      {funding.payment_explainer && (
                        <div style={{ fontSize: '0.78rem', color: C.textMid, marginTop: 4 }}><ReactMarkdown components={sectionMarkdownComponents}>{funding.payment_explainer}</ReactMarkdown></div>
                      )}
                    </div>
                  )}
                  {funding.funding_value_limit && (
                    <div style={{ background: C.pale, border: `1px solid ${C.border}`, borderRadius: 6, padding: '12px 14px' }}>
                      <div style={{ fontSize: '0.66rem', fontWeight: 600, letterSpacing: '1.2px', textTransform: 'uppercase', color: C.textMid, marginBottom: 5 }}>
                        Payment limit
                      </div>
                      <div style={{ fontSize: '1.1rem', color: C.primaryDeep, fontWeight: 600, paddingTop: 4 }}>
                        {funding.funding_value_limit_value
                          ? (funding.funding_value_limit_unit === '£'
                            ? fmtCurrency(funding.funding_value_limit_value)
                            : `${funding.funding_value_limit_value}${funding.funding_value_limit_unit ? ' ' + funding.funding_value_limit_unit : ''}`)
                          : funding.funding_value_limit}
                      </div>
                      {funding.funding_value_limit_explainer && (
                        <div style={{ fontSize: '0.78rem', color: C.textMid, marginTop: 4 }}><ReactMarkdown components={sectionMarkdownComponents}>{funding.funding_value_limit_explainer}</ReactMarkdown></div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Expected costs */}
            {expectedCosts.length > 0 && (
              <>
                <SubHeading style={{ fontSize: '0.66rem', letterSpacing: '1.4px', color: C.textMid, borderBottom: 'none', paddingBottom: 0, marginBottom: 2 }}>Expected Costs · <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>Indicative costs that could be expected — actual amounts will vary.</span></SubHeading>
                {expectedCosts.map((c, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    {c.header && <p style={{ fontSize: '0.85rem', fontWeight: 600, color: C.textDark, marginBottom: 2 }}>{c.header}</p>}
                    {c.detail && <ReactMarkdown components={sectionMarkdownComponents}>{c.detail}</ReactMarkdown>}
                  </div>
                ))}
              </>
            )}

            {/* Ineligible costs */}
            {ineligibleCosts.length > 0 && (
              <>
                <SubHeading style={{ fontSize: '0.66rem', letterSpacing: '1.4px', color: C.textMid, borderBottom: 'none', paddingBottom: 0 }}>Ineligible Costs</SubHeading>
                {ineligibleCosts.map((c, i) => (
                  <div key={i} style={{ marginBottom: 8 }}>
                    {c.header && <p style={{ fontSize: '0.85rem', fontWeight: 600, color: C.textDark, marginBottom: 2 }}>{c.header}</p>}
                    {c.detail && <ReactMarkdown components={sectionMarkdownComponents}>{c.detail}</ReactMarkdown>}
                  </div>
                ))}
              </>
            )}
          </SectionBlock>
        )}

        {hasCompatible && (
          <SectionBlock
            id="compatible" icon="🔗"
            title="Compatible Opportunities"
            hint="Other grants and actions that can be used alongside this opportunity"
            badge={(() => {
              const codeCount = (funding.compatible_grants_display || []).reduce((acc, col) => acc + (col.codes?.length || 0), 0);
              const resolvedCount = Object.values(funding.compatible_schemes || {}).reduce((acc, schemes) => acc + schemes.length, 0);
              const total = codeCount + resolvedCount;
              return total > 0 ? `${total} opportunities` : undefined;
            })()}
            openSections={openSections} onToggle={toggleSection}
          >
            {/* Code-based compatible collections */}
            {funding.compatible_grants_display?.map((col, i) => (
              <div key={i} style={{ marginBottom: 14 }}>
                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: '0.66rem', fontWeight: 600, letterSpacing: '1.4px', textTransform: 'uppercase', color: C.textMid }}>{col.collection_name}</span>
                  {col.all_compatible_rule && (
                    <ReactMarkdown components={sectionMarkdownComponents}>{col.all_compatible_rule}</ReactMarkdown>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {col.codes?.map((code, j) => (
                    <div key={j} style={{
                      background: C.pale, border: `1px solid ${C.border}`,
                      borderRadius: 6, padding: '8px 12px',
                      fontSize: '0.82rem', color: C.textMid,
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <span style={{ fontWeight: 700, color: C.primary, fontSize: '0.75rem', minWidth: 42 }}>
                        {typeof code === 'object' ? code.code : code}
                      </span>
                      {typeof code === 'object' && code.tag && <span>{code.tag}</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Resolved compatible schemes (grouped by fund_pot) */}
            {Object.entries(funding.compatible_schemes || {}).map(([pot, schemes]) => (
              <div key={pot} style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
                <button
                  onClick={() => togglePot(pot)}
                  style={{
                    width: '100%', padding: '12px 16px', background: C.pale,
                    border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 600, color: C.textDark, fontSize: '0.88rem' }}>{pot}</span>
                    <span style={{ fontSize: '0.72rem', background: C.primaryLight, color: C.primary, borderRadius: 12, padding: '1px 8px' }}>
                      {schemes.length}
                    </span>
                    <StatusPieChart
                      statuses={schemes.reduce((acc, s) => {
                        const st = s.status || 'not_specified';
                        acc[st] = (acc[st] || 0) + 1;
                        return acc;
                      }, {})}
                      size={20}
                    />
                  </div>
                  <svg style={{ width: 16, height: 16, color: C.textMid, transform: expandedPots[pot] ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {expandedPots[pot] && (
                  <div style={{ borderTop: `1px solid ${C.border}` }}>
                    {schemes.map(scheme => <FundingRow key={scheme.id} funding={scheme} compact />)}
                  </div>
                )}
              </div>
            ))}
          </SectionBlock>
        )}

        {/* ── Application sections ─────────────────────────────────────── */}
        <TierLabel>Application</TierLabel>

        {hasWhoCanApply && (
          <SectionBlock
            id="whoCanApply" icon="✅"
            title="Who Can Apply"
            hint="Eligibility requirements and restrictions"
            openSections={openSections} onToggle={toggleSection}
          >
            {funding.who_can_apply && (
              <InfoBox>
                <ReactMarkdown components={infoBoxMarkdownComponents}>{funding.who_can_apply}</ReactMarkdown>
              </InfoBox>
            )}



            {funding.land_types?.filter(t => t && t.toLowerCase() !== 'not specified').length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: '0.66rem', fontWeight: 600, letterSpacing: '1.4px', textTransform: 'uppercase', color: C.textMid, marginBottom: 6 }}>Eligible Land Types</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {funding.land_types.map((t, i) => (
                    <span key={i} style={{ background: C.primaryLight, color: C.primary, borderRadius: 20, padding: '4px 12px', fontSize: '0.75rem', fontWeight: 500, border: `1px solid rgba(97,93,236,0.2)` }}>{t}</span>
                  ))}
                </div>
              </div>
            )}

            {funding.land_covers?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: '0.66rem', fontWeight: 600, letterSpacing: '1.4px', textTransform: 'uppercase', color: C.textMid, marginBottom: 6 }}>RPA Land Cover</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {funding.land_covers.map((t, i) => (
                    <span key={i} style={{ background: C.primaryLight, color: C.primary, borderRadius: 20, padding: '4px 12px', fontSize: '0.75rem', fontWeight: 500, border: `1px solid rgba(97,93,236,0.2)` }}>{t}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Limited area meta */}
            {funding.limited_area === true && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 18, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
                <div style={{ background: C.pale, border: `1px solid ${C.border}`, borderRadius: 6, padding: '10px 12px' }}>
                  <div style={{ fontSize: '0.66rem', fontWeight: 600, letterSpacing: '1.4px', textTransform: 'uppercase', color: C.textMid, marginBottom: 4 }}>
                    📄 Limited Area
                  </div>
                  <span style={{
                    display: 'inline-block', padding: '3px 12px', borderRadius: 20,
                    fontSize: '0.78rem', fontWeight: 600,
                    background: funding.limited_area ? '#e8f5e9' : '#fdecea',
                    color: funding.limited_area ? '#2e7d32' : '#c62828',
                    border: `1px solid ${funding.limited_area ? '#c8e6c9' : '#f5c6c2'}`,
                  }}>
                    {funding.limited_area ? 'Yes' : 'No'}
                  </span>
                </div>
                {funding.limited_area_percentage != null && (
                  <div style={{ background: C.pale, border: `1px solid ${C.border}`, borderRadius: 6, padding: '10px 12px' }}>
                    <div style={{ fontSize: '0.66rem', fontWeight: 600, letterSpacing: '1.4px', textTransform: 'uppercase', color: C.textMid, marginBottom: 4 }}>
                      📊 Limited Area %
                    </div>
                    <span style={{ display: 'inline-block', padding: '3px 12px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600, background: C.primaryLight, color: C.primary, border: `1px solid rgba(97,93,236,0.2)` }}>
                      {funding.limited_area_percentage}%
                    </span>
                  </div>
                )}
              </div>
            )}

            {funding.eligibility_requirements && (
              <>
                <SubHeading style={{ fontSize: '0.66rem', letterSpacing: '1.4px', color: C.textMid, borderBottom: 'none', paddingBottom: 0 }}>Eligibility Requirements</SubHeading>
                <ReactMarkdown components={sectionMarkdownComponents}>{toMarkdownString(funding.eligibility_requirements)}</ReactMarkdown>
              </>
            )}
            {funding.ineligibility_requirements && (
              <>
                <div style={{ borderTop: '1px solid rgba(0,0,0,0.07)', margin: '14px 0 0' }} />
                <SubHeading style={{ fontSize: '0.66rem', letterSpacing: '1.4px', color: C.textMid, borderBottom: 'none', paddingBottom: 0 }}>Ineligibility</SubHeading>
                <ReactMarkdown components={sectionMarkdownComponents}>{toMarkdownString(funding.ineligibility_requirements)}</ReactMarkdown>
              </>
            )}
          </SectionBlock>
        )}

        {hasHowToApply && (
          <SectionBlock
            id="howToApply" icon="🚀"
            title="How To Apply"
            hint="Step-by-step application process"
            openSections={openSections} onToggle={toggleSection}
          >
            <ReactMarkdown components={howToApplyComponents}>{funding.how_to_apply}</ReactMarkdown>
          </SectionBlock>
        )}

        {hasCriteria && (
          <SectionBlock
            id="criteria" icon="📊"
            title="Application Criteria"
            hint="Scoring, grant amounts, and item specifications"
            openSections={openSections} onToggle={toggleSection}
          >
            <ReactMarkdown components={sectionMarkdownComponents}>{toMarkdownString(funding.application_criteria)}</ReactMarkdown>
          </SectionBlock>
        )}

        {hasConsents && (
          <SectionBlock
            id="consents" icon="📜"
            title="Consents & Permissions"
            hint="Protected site eligibility by land type"
            openSections={openSections} onToggle={toggleSection}
          >
            {funding.consent_text && (
              <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
                <ReactMarkdown components={sectionMarkdownComponents}>{funding.consent_text}</ReactMarkdown>
              </div>
            )}
            {funding.protected_land?.length > 0 && (() => {
              const manualLand = funding.protected_land.filter(l => !l.is_default);
              const autoLand   = funding.protected_land.filter(l => l.is_default);
              const renderTable = (rows) => (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                  <thead>
                    <tr>
                      {['Site / Land Type', 'Status'].map(h => (
                        <th key={h} style={{
                          background: C.pale, padding: '7px 12px', textAlign: 'left',
                          fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.8px',
                          textTransform: 'uppercase', color: C.textMid,
                          borderBottom: `2px solid ${C.border}`,
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((land, i) => {
                      const pill = protectedLandPill(land.status);
                      return (
                        <tr key={i} style={{ borderBottom: `1px solid #f0f0f0` }}
                          onMouseEnter={e => e.currentTarget.style.background = C.pale}
                          onMouseLeave={e => e.currentTarget.style.background = ''}
                        >
                          <td style={{ padding: '8px 12px', color: C.textMid }}>{land.name}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '3px 9px', borderRadius: 20,
                              fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap',
                              background: pill.bg, color: pill.color, border: `1px solid ${pill.border}`,
                            }}>
                              {pill.icon} {pill.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
              return (
                <>
                  {manualLand.length > 0 && renderTable(manualLand)}
                  {autoLand.length > 0 && (
                    <div style={{ marginTop: manualLand.length > 0 ? 14 : 0 }}>
                      <button
                        onClick={() => setShowAutoProtectedLand(v => !v)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 6,
                          fontSize: '0.78rem', fontWeight: 600, color: C.textMid,
                          padding: '6px 0', fontFamily: 'inherit',
                        }}
                      >
                        <span style={{ fontSize: '0.6rem', transition: 'transform 0.15s', display: 'inline-block', transform: showAutoProtectedLand ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                        Other eligible protected sites ({autoLand.length})
                      </button>
                      {showAutoProtectedLand && (
                        <div style={{ marginTop: 8 }}>{renderTable(autoLand)}</div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </SectionBlock>
        )}

        {hasContacts && (
          <SectionBlock
            id="contacts" icon="📞"
            title="Contact Information"
            hint="Get in touch or find the official application page"
            openSections={openSections} onToggle={toggleSection}
          >
            {funding.contacts?.length > 0 && (() => {
              const getRoleLabel = (role) => {
                const r = (role || '').toLowerCase();
                if (r.includes('funder')) return 'Funded by:';
                if (r.includes('delivery') || r.includes('partner')) return 'Delivered by:';
                return role;
              };
              const groups = funding.contacts.reduce((acc, c) => {
                const key = c.role || '';
                if (!acc[key]) acc[key] = [];
                acc[key].push(c);
                return acc;
              }, {});
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 16 }}>
                  {Object.entries(groups).map(([role, contacts]) => (
                    <div key={role}>
                      {role && (
                        <div style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '1.2px', textTransform: 'uppercase', color: C.textMid, marginBottom: 8 }}>
                          {getRoleLabel(role)}
                        </div>
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {contacts.map((c, i) => (
                          <div key={i} style={{ background: C.pale, border: `1px solid ${C.border}`, borderRadius: 6, padding: '14px 16px' }}>
                            {c.name && <div style={{ fontSize: '0.85rem', fontWeight: 600, color: C.textDark, marginBottom: 2 }}>{c.name}</div>}
                            {c.contact && <div style={{ fontSize: '0.8rem', color: C.textMid, marginBottom: 4 }}>📋 {c.contact}</div>}
                            {c.email && (
                              <div style={{ fontSize: '0.8rem', color: C.textMid, marginBottom: 4 }}>
                                ✉️ <a href={`mailto:${c.email}`} style={{ color: C.primary, textDecoration: 'none' }}>{c.email}</a>
                              </div>
                            )}
                            {c.phone && (
                              <div style={{ fontSize: '0.8rem', color: C.textMid, marginBottom: 4 }}>
                                📞 <a href={`tel:${c.phone}`} style={{ color: C.primary, textDecoration: 'none' }}>{c.phone}</a>
                              </div>
                            )}
                            {c.availability && <div style={{ fontSize: '0.78rem', color: C.textMid, marginBottom: 4 }}>🕐 {c.availability}</div>}
                            {c.url && (
                              <div style={{ fontSize: '0.8rem', color: C.textMid }}>
                                🔗 <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: C.primary, textDecoration: 'none' }}>{c.url}</a>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

          </SectionBlock>
        )}

        {/* ── Source footer ────────────────────────────────────────────── */}
        <div style={{
          marginTop: 28, padding: '14px 20px',
          background: C.pale, border: `1px solid ${C.border}`,
          borderRadius: 10, display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          fontSize: '0.75rem', color: C.textMid, flexWrap: 'wrap', gap: 8,
        }}>
          <span>
            {funding.last_updated_by_oko && (
              <>Last updated by Oko: <strong style={{ color: C.textDark }}>{formatDate(funding.last_updated_by_oko)}</strong>&nbsp;·&nbsp;</>
            )}
            {funding.last_updated && (
              <>Source last updated: <strong style={{ color: C.textDark }}>{formatDate(funding.last_updated)}</strong></>
            )}
            {funding.review_status && (
              <>
                &nbsp;·&nbsp;AI-generated Content Review Status:{' '}
                <strong style={{ color: C.textDark }}>
                  {funding.review_status === 'awaiting_review' && 'Awaiting Review'}
                  {funding.review_status === 'reviewed_by_team' && 'Reviewed by Our Team'}
                  {funding.review_status === 'confirmed_by_funder' && 'Confirmed by Funder'}
                </strong>
              </>
            )}
          </span>
        </div>

        {/* Official guidance & Report Issue */}
        <div className="pdf-hidden" style={{ marginTop: 20, display: 'flex', justifyContent: 'center', gap: 16, alignItems: 'center' }}>
          {funding.application_url && (
            <a
              href={funding.application_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
              style={{ textDecoration: 'none' }}
            >
              🔗  Official guidance page
            </a>
          )}
          <button
            onClick={() => setShowFeedbackForm(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Report an Issue
          </button>
        </div>

      </div>

      {showFeedbackForm && (
        <FeedbackForm
          fundingScheme={funding}
          onClose={() => setShowFeedbackForm(false)}
        />
      )}

      {showPrintModal && (
        <PrintSectionModal
          onPrint={handlePrint}
          onCancel={() => setShowPrintModal(false)}
        />
      )}

      {/* Hidden print layout — rendered off-screen, used by handlePrint */}
      {printSections && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <FundingPrintLayout ref={printRef} funding={funding} sections={printSections} />
        </div>
      )}
    </div>
  );
};

export default FundingDetail;
